import type { CharacterWithStats, NamedText } from '@shared/types'
import { Panel, SectionHeading } from '@/components/ui'

const COIN_ORDER = ['pp', 'gp', 'ep', 'sp', 'cp'] as const

export function AttacksPanel({
  character, onRoll,
}: {
  character: CharacterWithStats
  onRoll?: (formula: string, context: string) => void
}) {
  if (character.attacks.length === 0) return null

  return (
    <Panel className="p-4">
      <SectionHeading>
        Attacks
        {onRoll && <span className="ml-2 normal-case tracking-normal text-ink-500">— tap to roll</span>}
      </SectionHeading>
      <ul className="divide-y divide-ink-700/50">
        {character.attacks.map((attack) => (
          <li key={attack.name} className="py-1 first:pt-0 last:pb-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-1">
              <span className="min-w-0 flex-1 truncate text-sm text-ink-100">{attack.name}</span>
              {onRoll ? (
                <>
                  <button
                    type="button"
                    title={`Roll to hit with ${attack.name}`}
                    onClick={() => onRoll(toHitFormula(attack.bonus), `${attack.name} attack`)}
                    className="rounded-md px-1.5 font-mono text-sm tabular-nums text-gold-400 transition-colors hover:bg-gold-900/40"
                  >
                    {attack.bonus}
                  </button>
                  <button
                    type="button"
                    title={`Roll ${attack.name} damage`}
                    onClick={() => onRoll(damageFormula(attack.damage), `${attack.name} damage`)}
                    className="rounded-md px-1.5 font-mono text-sm text-ink-300 transition-colors hover:bg-gold-900/40 hover:text-gold-400"
                  >
                    {attack.damage}
                  </button>
                </>
              ) : (
                <>
                  <span className="font-mono text-sm tabular-nums text-gold-400">{attack.bonus}</span>
                  <span className="font-mono text-sm text-ink-300">{attack.damage}</span>
                </>
              )}
            </div>
            {attack.notes && <p className="pb-1 text-[11px] text-ink-500">{attack.notes}</p>}
          </li>
        ))}
      </ul>
    </Panel>
  )
}

/** "+5" -> "1d20+5". Attack bonuses on a sheet are already signed. */
function toHitFormula(bonus: string): string {
  const cleaned = bonus.trim().replace(/[−–—]/g, '-')
  if (!cleaned) return '1d20'
  return /^[+-]/.test(cleaned) ? `1d20${cleaned}` : `1d20+${cleaned}`
}

/** "1d6+1 bludgeon" -> "1d6+1": strip the damage type before rolling. */
function damageFormula(damage: string): string {
  const match = damage.trim().replace(/[−–—]/g, '-').match(/^[\dd+\-\s]+/i)
  return (match?.[0] ?? damage).replace(/\s+/g, '') || '1d4'
}

export function TraitsPanel({ title, entries }: { title: string; entries: NamedText[] }) {
  if (entries.length === 0) return null
  return (
    <Panel className="p-4">
      <SectionHeading>{title}</SectionHeading>
      <ul className="space-y-2.5">
        {entries.map((entry) => (
          <li key={entry.name} className="text-sm">
            <span className="font-medium text-ink-100">{entry.name}. </span>
            <span className="leading-relaxed text-ink-400">{entry.text}</span>
          </li>
        ))}
      </ul>
    </Panel>
  )
}

export function EquipmentPanel({ character }: { character: CharacterWithStats }) {
  const coins = COIN_ORDER.filter((key) => character.coins[key] > 0)
  if (character.equipment.length === 0 && coins.length === 0) return null

  return (
    <Panel className="p-4">
      <SectionHeading
        action={
          coins.length > 0 && (
            <span className="font-mono text-xs text-gold-400">
              {coins.map((key) => `${character.coins[key]} ${key}`).join(' · ')}
            </span>
          )
        }
      >
        Equipment
      </SectionHeading>
      {/* Columns flow down then across so pack contents stay under their pack. */}
      <div className="columns-1 gap-6 text-sm text-ink-300 sm:columns-2">
        {character.equipment.map((item, index) => (
          <p key={`${item}-${index}`} className={item.startsWith('  ') ? 'pl-3 text-ink-500' : ''}>
            {item.trim()}
          </p>
        ))}
      </div>
    </Panel>
  )
}

export function ProficienciesPanel({ character }: { character: CharacterWithStats }) {
  const { armorTraining, weaponTraining, otherProficiencies } = character
  const armor = (['light', 'medium', 'heavy', 'shields'] as const).filter((key) => armorTraining[key])

  const rows = [
    armor.length > 0 && ['Armor training', armor.map(capitalize).join(', ')],
    weaponTraining && ['Weapons', weaponTraining],
    otherProficiencies.tools.length > 0 && ['Tools', otherProficiencies.tools.join(', ')],
    otherProficiencies.languages.length > 0 && ['Languages', otherProficiencies.languages.join(', ')],
  ].filter(Boolean) as [string, string][]

  if (rows.length === 0) return null

  return (
    <Panel className="p-4">
      <SectionHeading>Training &amp; proficiencies</SectionHeading>
      <dl className="space-y-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex flex-wrap gap-x-3 text-sm">
            <dt className="w-28 shrink-0 text-xs uppercase tracking-wider text-ink-500">{label}</dt>
            <dd className="min-w-0 flex-1 text-ink-300">{value}</dd>
          </div>
        ))}
      </dl>
    </Panel>
  )
}

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1)
